import { useEffect, useRef, useState, useCallback } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { formatDistanceToNow } from 'date-fns';

/**
 * [T68P1-a] Auto-save form state to localStorage, restore on re-open.
 *
 * Features:
 * - Debounced (800ms) auto-save on every field change
 * - Restore draft on mount when sheet opens (if draft < 24h old)
 * - Shows toast when draft is restored
 * - `clearDraft()` to manually remove stored draft
 * - `draftAge` for display in discard dialog
 *
 * @param formId  Unique identifier for the form instance (e.g., 'transaction-create')
 * @param userId  Current user ID (or undefined for anonymous)
 * @param form    react-hook-form's UseFormReturn
 * @param enabled Whether drafting is active (false when editing existing entity)
 */

export const DRAFT_KEY_PREFIX = 'draft:';

interface DraftData<T> {
  values: T;
  savedAt: number;
}

const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours
const DEBOUNCE_MS = 800;

export function useFormDraft<T extends FieldValues>(
  formId: string,
  userId: string | undefined,
  form: UseFormReturn<T>,
  enabled: boolean = true
): {
  hasDraft: boolean;
  draftAge: string | null;
  clearDraft: () => void;
  saveDraftNow: () => void;
  /** True from the moment a draft is restored until `dismissRestore` is called (or 6 s). */
  justRestored: boolean;
  /** Human-readable age of the restored draft (e.g. "3 minutes ago"). */
  restoredAge: string | null;
  /** Call to dismiss the DraftRestoredBanner manually. */
  dismissRestore: () => void;
} {
  const [hasDraft, setHasDraft] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [justRestored, setJustRestored] = useState(false);
  const [restoredAge, setRestoredAge] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const restoredRef = useRef(false);
  const prevEnabledRef = useRef(enabled);

  const storageKey = `${DRAFT_KEY_PREFIX}${formId}:${userId ?? 'anon'}`;

  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // localStorage not available
    }
    setHasDraft(false);
    setSavedAt(null);
    setJustRestored(false);
    setRestoredAge(null);
  }, [storageKey]);

  const dismissRestore = useCallback(() => {
    setJustRestored(false);
  }, []);

  // Reset restoredRef each time the form closes (enabled: true → false),
  // so the next open-cycle will re-restore the draft and show the banner again.
  useEffect(() => {
    if (prevEnabledRef.current && !enabled) {
      restoredRef.current = false;
      setJustRestored(false);
    }
    prevEnabledRef.current = enabled;
  }, [enabled]);

  // Restore draft on mount (only once per open cycle)
  useEffect(() => {
    if (!enabled || restoredRef.current) return;
    restoredRef.current = true;

    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;

      const draft: DraftData<T> = JSON.parse(raw);

      // Check age
      if (Date.now() - draft.savedAt > DRAFT_MAX_AGE_MS) {
        localStorage.removeItem(storageKey);
        return;
      }

      // Deserialize date fields (ISO strings → Date objects)
      const values = deserializeDates(draft.values);

      form.reset(values as T);
      setHasDraft(true);
      setSavedAt(draft.savedAt);

      const age = formatDistanceToNow(new Date(draft.savedAt), { addSuffix: true });
      // Signal the inline DraftRestoredBanner (inside the form Sheet) instead of
      // a floating toast — the toast at top-16 right-4 collides with the Sheet
      // header on mobile (w-full before md: breakpoint).
      setJustRestored(true);
      setRestoredAge(age);
    } catch {
      // Invalid JSON or localStorage error — ignore
    }
  }, [enabled, storageKey, form]);

  // Subscribe to form changes and auto-save (debounced)
  useEffect(() => {
    if (!enabled) return;

    const subscription = form.watch((values) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);

      debounceRef.current = setTimeout(() => {
        try {
          const draft: DraftData<T> = {
            values: values as T,
            savedAt: Date.now(),
          };
          localStorage.setItem(storageKey, JSON.stringify(draft));
          setHasDraft(true);
          setSavedAt(draft.savedAt);
        } catch {
          // localStorage full or not available
        }
      }, DEBOUNCE_MS);
    });

    return () => {
      subscription.unsubscribe();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [enabled, form, storageKey]);

  const draftAge = savedAt
    ? formatDistanceToNow(new Date(savedAt), { addSuffix: true })
    : null;

  /** Immediately flush current form values to localStorage (no debounce). */
  const saveDraftNow = useCallback(() => {
    if (!enabled) return;
    try {
      const draft: DraftData<T> = {
        values: form.getValues(),
        savedAt: Date.now(),
      };
      localStorage.setItem(storageKey, JSON.stringify(draft));
      setHasDraft(true);
      setSavedAt(draft.savedAt);
    } catch {
      // localStorage full or not available
    }
  }, [enabled, form, storageKey]);

  return { hasDraft, draftAge, clearDraft, saveDraftNow, justRestored, restoredAge, dismissRestore };
}

/**
 * Recursively convert ISO date strings back to Date objects.
 * Handles common patterns like 'targetDate', 'date', etc.
 */
function deserializeDates<T>(obj: T): T {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj === 'string') {
    // ISO 8601 date string pattern
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(obj)) {
      const parsed = new Date(obj);
      if (!isNaN(parsed.getTime())) return parsed as unknown as T;
    }
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(deserializeDates) as unknown as T;
  }
  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      result[key] = deserializeDates(value);
    }
    return result as T;
  }
  return obj;
}

export default useFormDraft;

/**
 * [T73P3-a] Prune orphaned draft keys from localStorage.
 * Removes any `draft:*` keys whose `savedAt` timestamp is older than 24 hours.
 * Intended to be called once on app startup.
 */
export function pruneOrphanedDrafts(): void {
  try {
    const now = Date.now();
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(DRAFT_KEY_PREFIX)) continue;
      try {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        const data = JSON.parse(raw) as { savedAt?: number };
        if (typeof data.savedAt === 'number' && now - data.savedAt > DRAFT_MAX_AGE_MS) {
          localStorage.removeItem(key);
        }
      } catch {
        // Malformed entry — remove it
        localStorage.removeItem(key);
      }
    }
  } catch {
    // localStorage not available
  }
}
